<template>
  <div>
    <transition name="fade">
      <div v-show="focus && mask" class="lp-input-mask"></div>
    </transition>
    <lp-label
      v-if="labelTop"
      class="lp-input-label"
      :preview="preview"
      :required="required"
      :is-error="!!message"
    >
      {{ labelTop }}
    </lp-label>
    <div
      class="lp-input-prefix-wrapper ly-row"
      :style="
        showCount || message || helper || size === 'compact' || compact
          ? ''
          : 'padding-bottom: 22px;'
      "
    >
      <div
        v-if="forcePrefix || (!preview && prefix)"
        class="lp-input-prefix"
        :class="[preview && 'lp-input-prefix-preview']"
      >
        {{ prefix }}
      </div>
      <div
        class="lp-input-wrapper ly-row"
        :class="[
          !preview &&
            message &&
            `lp-input-wrapper-${message ? 'danger' : status}`,
          disabled && `lp-input-wrapper-disabled`,
          preview && 'lp-input-wrapper-preview',
          !preview && 'xpx-4',
        ]"
      >
        <span
          v-if="forcePrefixIcon || (!preview && prefixIcon)"
          class="lp-input-icon-wrapper"
        >
          <lp-icon
            v-if="prefixIcon"
            :aria-hidden="prefixIconAriaHidden"
            :name="prefixIcon"
          />
        </span>
        <span v-if="preview" class="lp-input-preview">
          {{ modelValue }}
        </span>
        <input
          v-else
          ref="inputElement"
          :data-ua-unique-id="dataUaUniqueId"
          v-bind="$attrs"
          :type="type"
          class="lp-input px-0"
          :disabled="disabled"
          :value="modelValue"
          :placeholder="placeholder"
          :maxlength="maxLength"
          :aria-label="placeholder"
          @input="input"
          @focus="focus = true"
          @blur="focus = false"
        />
        <div class="lp-input-actions ly-row ly-no-gap">
          <slot name="clear" />
          <span
            v-if="forceSuffixIcon || (!preview && suffixIcon)"
            ref="inputSuffixIcon"
            class="lp-input-icon-wrapper xpl-2"
            @click="focusInput"
          >
            <lp-icon
              v-if="suffixIcon"
              class="lp-input-icon"
              compact
              :name="suffixIcon"
            />
          </span>
        </div>
      </div>
      <div
        v-if="forceSuffix || (!preview && suffix)"
        class="lp-input-suffix"
        :class="[preview && 'lp-input-suffix-preview']"
      >
        {{ suffix }}
      </div>
    </div>
    <transition name="fade-bottom">
      <div
        v-if="
          helper ||
          forceMessage ||
          (!preview && message) ||
          (!preview && showCount && maxLength !== Infinity)
        "
        class="ly-row ly-spacebetween-center"
      >
        <span
          v-if="forceMessage || (!preview && message)"
          class="lp-input-message mt-half d-flex"
          :class="[
            message && `lp-input-message-${message ? 'danger' : status}`,
            disabled && `lp-input-message-disabled`,
          ]"
        >
          <lp-icon v-if="!preview && message" name="info" class="mr-half" />
          {{ message }}
        </span>
        <span v-if="!message" class="lp-input-message mt-half d-flex text-gray">
          {{ helper }}
        </span>
        <span
          v-if="!preview && showCount && maxLength !== Infinity"
          class="lp-input-message mt-half d-flex"
          :class="[
            message && `lp-input-message-${message ? 'danger' : status}`,
            disabled && `lp-input-message-disabled`,
          ]"
        >
          {{ `${charactersCount}/${maxLength}` }}
        </span>
      </div>
    </transition>
  </div>
</template>

<script lang="ts">
export default {
  name: 'lp-input',
  inheritAttrs: false,
}
</script>

<script setup lang="ts">
import { ref, nextTick } from 'vue'
import { Colors } from '@/models/enums'
import LpLabel from '@/components/label/LpLabel.vue'

const emit = defineEmits(['update:modelValue'])

interface Props {
  status?: string
  type?: string
  showCount?: boolean
  maxLength?: number | string
  size?: string
  message?: string | null
  helper?: string | null
  placeholder?: string | null
  disabled?: boolean
  labelTop?: string | null
  modelValue: number | null | string
  prefix?: string | null
  suffix?: string | null
  prefixIcon?: string | null
  suffixIcon?: string | null
  required?: boolean | null
  mask?: boolean | null
  compact?: boolean | null
  preview?: boolean | null
  forcePrefix?: boolean | null
  forcePrefixIcon?: boolean | null
  forceSuffixIcon?: boolean | null
  forceSuffix?: boolean | null
  forceMessage?: boolean | null
  dataUaUniqueId?: string
  prefixIconAriaHidden?: string
}

withDefaults(defineProps<Props>(), {
  status: Colors.NEUTRAL,
  type: 'text',
  showCount: false,
  maxLength: Infinity,
  size: 'default',
  message: null,
  placeholder: null,
  helper: null,
  disabled: false,
  labelTop: null,
  modelValue: null,
  prefix: null,
  suffix: null,
  prefixIcon: null,
  suffixIcon: null,
  required: false,
  mask: false,
  compact: false,
  preview: false,
  forcePrefix: false,
  forcePrefixIcon: false,
  forceSuffixIcon: false,
  forceSuffix: false,
  forceMessage: false,
  dataUaUniqueId: 'unique-input',
  prefixIconAriaHidden: 'false',
})
const focus = ref(false)
const inputElement = ref<HTMLInputElement | null>(null)
const charactersCount = ref(0)

function input(e: Event) {
  emit('update:modelValue', (e.target as HTMLInputElement).value)
  charactersCount.value = (e.target as HTMLInputElement).value.length
}

async function focusInput() {
  await nextTick(() => {
    const input = inputElement.value
    if (input) {
      input.focus()
    }
  })
}
</script>
